import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'
import { SPARKLE_PATH } from '@/components/landing/decor/sparkle'

export type PatternVariant = 'sky' | 'night' | 'tower' | 'observatory' | 'orbit'

interface PatternArtProps {
    variant?: PatternVariant
    seed?: string
    className?: string
}

const VARIANTS: Array<PatternVariant> = ['sky', 'orbit', 'observatory', 'night', 'tower']

const hash = (value: string): number => {
    let result = 0

    for (let index = 0; index < value.length; index++) result = (result * 31 + value.charCodeAt(index)) >>> 0

    return result
}

const spark = (x: number, y: number, size: number, fill: string): ReactNode => (
    <path d={SPARKLE_PATH} fill={fill} transform={`translate(${x - size / 2} ${y - size / 2}) scale(${Math.round((size / 24) * 100) / 100})`} />
)

const plus = (x: number, y: number, size = 6): string => `M${x - size} ${y}H${x + size}M${x} ${y - size}V${y + size}`

// Small geometric compositions in the site's palette (sky, orbits, outlines of the observatory
// and the old tower, a night with constellations). Used for the STORY record cards and as the
// artwork shown when a stream or goods item has no image of its own yet.
const PatternArt: FC<PatternArtProps> = ({ variant, seed = '', className }): ReactNode => {
    const id = `pa${useId().replace(/[^a-zA-Z0-9]/g, '')}`
    const kind = variant ?? VARIANTS[hash(seed) % VARIANTS.length]
    const night = kind === 'night'

    return (
        <svg viewBox='0 0 400 300' preserveAspectRatio='xMidYMid slice' aria-hidden='true' focusable='false' className={cn('block', className)}>
            <defs>
                <linearGradient id={`${id}-bg`} x1='0' y1='0' x2='0' y2='1'>
                    {night
                        ? (
                            <>
                                <stop offset='0%' stopColor='#2c3a5e' />
                                <stop offset='100%' stopColor='#141b2e' />
                            </>
                        )
                        : (
                            <>
                                <stop offset='0%' stopColor={kind === 'sky' || kind === 'orbit' ? '#c6e1f1' : '#f6f2e8'} />
                                <stop offset='100%' stopColor='#fbfaf6' />
                            </>
                        )}
                </linearGradient>
                <pattern id={`${id}-grid`} width='20' height='20' patternUnits='userSpaceOnUse'>
                    <path d='M0 0.5H20M0.5 0V20' stroke={night ? '#fbfaf6' : '#1d2a33'} strokeOpacity={night ? 0.06 : 0.06} />
                </pattern>
            </defs>

            <rect width='400' height='300' fill={`url(#${id}-bg)`} />
            <rect width='400' height='300' fill={`url(#${id}-grid)`} />

            {kind === 'sky' && (
                <g>
                    <circle cx='290' cy='96' r='54' fill='#fff7dc' stroke='#e2b33c' strokeOpacity='0.6' />
                    <circle cx='290' cy='96' r='82' fill='none' stroke='#e2b33c' strokeOpacity='0.4' strokeDasharray='2 6' />
                    <path d='M0 214H400' stroke='#1d2a33' strokeOpacity='0.22' />
                    <path d='M0 214L120 168L210 196L310 150L400 182V300H0Z' fill='#fdfcf8' stroke='#1d2a33' strokeOpacity='0.2' />
                    <path d='M-20 0H80L220 300H120Z' fill='#ffffff' opacity='0.35' />
                    {spark(96, 72, 16, '#e2b33c')}
                    <path d={`${plus(160, 46)}${plus(52, 140, 5)}`} stroke='#1d2a33' strokeOpacity='0.35' />
                </g>
            )}

            {kind === 'orbit' && (
                <g fill='none' stroke='#1d2a33'>
                    <circle cx='200' cy='170' r='46' strokeOpacity='0.3' fill='#fdfcf8' />
                    <circle cx='200' cy='170' r='86' strokeOpacity='0.2' />
                    <circle cx='200' cy='170' r='126' strokeOpacity='0.14' strokeDasharray='2 7' />
                    <ellipse cx='200' cy='170' rx='170' ry='54' strokeOpacity='0.2' transform='rotate(-14 200 170)' />
                    <circle cx='325' cy='138' r='6' fill='#e2b33c' stroke='none' />
                    <path d={`${plus(70, 70)}${plus(330, 250, 5)}`} strokeOpacity='0.35' />
                    <g stroke='none'>{spark(200, 170, 30, '#e2b33c')}</g>
                </g>
            )}

            {kind === 'observatory' && (
                <g stroke='#1d2a33' strokeWidth='1.2'>
                    <circle cx='300' cy='84' r='38' fill='#fff7dc' stroke='#e2b33c' strokeOpacity='0.55' />
                    <path d='M110 300V196H290V300Z' fill='#fdfcf8' strokeOpacity='0.3' />
                    <path d='M126 196A74 74 0 0 1 274 196Z' fill='#fdfcf8' strokeOpacity='0.3' />
                    <path d='M192 124V196M208 124V196' strokeOpacity='0.3' fill='none' />
                    <path d='M126 230H274M126 258H274' strokeOpacity='0.16' strokeDasharray='4 5' fill='none' />
                    <path d='M0 268H400' strokeOpacity='0.2' fill='none' />
                    <path d={`${plus(60, 60)}${plus(350, 200, 5)}`} strokeOpacity='0.35' fill='none' />
                    <g stroke='none'>{spark(76, 150, 14, '#e2b33c')}</g>
                </g>
            )}

            {kind === 'tower' && (
                <g stroke='#1d2a33' strokeWidth='1.2'>
                    <circle cx='96' cy='80' r='34' fill='#fff7dc' stroke='#e2b33c' strokeOpacity='0.55' />
                    <path d='M168 300V70H262V300Z' fill='#fdfcf8' strokeOpacity='0.32' />
                    <path d='M262 300V120H312V300Z' fill='#f2eee4' strokeOpacity='0.28' />
                    <path d='M184 96H206V118H184ZM224 96H246V118H224ZM184 140H206V162H184ZM224 140H246V162H224ZM184 184H206V206H184Z' fill='none' strokeOpacity='0.22' />
                    <path d='M168 70L180 52M200 70L198 48M240 70L252 46' strokeOpacity='0.3' fill='none' />
                    <path d='M0 264H400' strokeOpacity='0.2' fill='none' />
                    <path d={`${plus(340, 70)}${plus(60, 190, 5)}`} strokeOpacity='0.35' fill='none' />
                    <g stroke='none'>{spark(330, 150, 12, '#e2b33c')}</g>
                </g>
            )}

            {kind === 'night' && (
                <g>
                    <path d='M296 70A34 34 0 1 0 330 120A26 26 0 1 1 296 70Z' fill='#f3d27a' opacity='0.9' />
                    <path d='M60 200L120 150L178 172L240 112L300 160' fill='none' stroke='#fbfaf6' strokeOpacity='0.35' strokeDasharray='2 5' />
                    {[[60, 200], [120, 150], [178, 172], [240, 112], [300, 160]].map(([x, y]) => (
                        <circle key={`${x}-${y}`} cx={x} cy={y} r='2.6' fill='#fbfaf6' />
                    ))}
                    {[[30, 40], [90, 90], [160, 60], [210, 36], [370, 210], [340, 260], [40, 270], [140, 250]].map(([x, y]) => (
                        <circle key={`d${x}-${y}`} cx={x} cy={y} r='1.4' fill='#fbfaf6' opacity='0.7' />
                    ))}
                    {spark(240, 112, 16, '#f3d27a')}
                    {spark(110, 236, 10, '#fbfaf6')}
                    <path d='M0 280H400' stroke='#fbfaf6' strokeOpacity='0.2' />
                </g>
            )}
        </svg>
    )
}

export default PatternArt
